'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { adminApi } from '@/api/admin';
import { useCandidate } from '@/contexts/CandidateContext';
import {
  FilterOptions,
  SearchFilters,
  CandidateSearchResult,
  PaginationData,
  MessageContent,
} from '@/types/admin';

interface FilterOptionsState {
  data: FilterOptions | null;
  isLoading: boolean;
  error: string | null;
}

interface SearchState {
  data: CandidateSearchResult[];
  isLoading: boolean;
  error: string | null;
  pagination: PaginationData;
}

interface SlidePanelState {
  isOpen: boolean;
}

interface AdminContextType {
  // Filter options
  filterOptions: FilterOptionsState;
  fetchFilterOptions: () => Promise<void>;

  // Search filters and results
  filters: SearchFilters;
  setFilters: (filters: SearchFilters) => void;
  clearFilters: () => void;
  searchResults: SearchState;
  searchCandidates: (page?: number, overrideFilters?: SearchFilters) => Promise<void>;

  // Selected candidates for bulk actions
  selectedCandidates: number[];
  setSelectedCandidates: (ids: number[]) => void;
  toggleCandidateSelection: (id: number) => void;
  clearSelectedCandidates: () => void;
  selectAllCandidates: () => void;

  // Slide panel
  slidePanel: SlidePanelState;
  openCandidatePanel: (candidateId: number) => Promise<void>;
  closeCandidatePanel: () => void;

  // Messaging
  sendBulkMessages: (
    candidateIds: number[],
    channels: ('email' | 'whatsapp')[],
    content: MessageContent
  ) => Promise<void>;
  isSendingMessages: boolean;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function AdminProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  // Delegate candidate detail fetching to CandidateContext so the slide panel
  // shares state (timeline, counts, notes) with every other authenticated
  // candidate flow. CandidateProvider wraps AdminProvider in AppProvider.
  const { getCandidateById: fetchCandidateInCtx } = useCandidate();

  // Filter options state
  const [filterOptions, setFilterOptions] = useState<FilterOptionsState>({
    data: null,
    isLoading: false,
    error: null,
  });

  // Search filters
  const [filters, setFiltersState] = useState<SearchFilters>({});

  // Search results state
  const [searchResults, setSearchResults] = useState<SearchState>({
    data: [],
    isLoading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 20,
      totalCount: 0,
      totalPages: 0,
    },
  });

  // Selected candidates for bulk actions
  const [selectedCandidates, setSelectedCandidatesState] = useState<number[]>([]);

  // Slide panel state -- only visibility; the candidate itself lives in
  // CandidateContext.
  const [slidePanel, setSlidePanelState] = useState<SlidePanelState>({
    isOpen: false,
  });

  // Messaging state
  const [isSendingMessages, setIsSendingMessages] = useState(false);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    setFilterOptions(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await adminApi.getFilters();
      setFilterOptions({
        data,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch filter options';
      setFilterOptions(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      toast({
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, [toast]);

  // Set filters
  const setFilters = useCallback((newFilters: SearchFilters) => {
    setFiltersState(newFilters);
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  // Search candidates
  const searchCandidates = useCallback(
    async (page: number = 1, overrideFilters?: SearchFilters) => {
      const filtersToUse = overrideFilters ?? filters;
      setSearchResults(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await adminApi.searchCandidates(
          filtersToUse,
          page,
          searchResults.pagination.limit
        );
        setSearchResults({
          data: response.candidates,
          isLoading: false,
          error: null,
          pagination: response.pagination,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to search candidates';
        setSearchResults(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          description: errorMessage,
          variant: 'destructive',
        });
      }
    },
    [filters, searchResults.pagination.limit, toast]
  );

  // Selected candidates management
  const setSelectedCandidates = useCallback((ids: number[]) => {
    setSelectedCandidatesState(ids);
  }, []);

  const toggleCandidateSelection = useCallback((id: number) => {
    setSelectedCandidatesState(prev => {
      if (prev.includes(id)) {
        return prev.filter(candidateId => candidateId !== id);
      }
      return [...prev, id];
    });
  }, []);

  const clearSelectedCandidates = useCallback(() => {
    setSelectedCandidatesState([]);
  }, []);

  const selectAllCandidates = useCallback(() => {
    const allIds = searchResults.data.map(c => c.id);
    setSelectedCandidatesState(allIds);
  }, [searchResults.data]);

  // Slide panel management
  const openCandidatePanel = useCallback(
    async (candidateId: number) => {
      setSlidePanelState({ isOpen: true });

      try {
        // Drive the fetch through CandidateContext so the slide panel's tabs
        // and timeline read from the shared candidate state. Error/loading
        // state is also owned by CandidateContext.
        await fetchCandidateInCtx(candidateId);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch candidate details';
        toast({
          description: errorMessage,
          variant: 'destructive',
        });
        setSlidePanelState({ isOpen: false });
      }
    },
    [fetchCandidateInCtx, toast]
  );

  const closeCandidatePanel = useCallback(() => {
    setSlidePanelState({ isOpen: false });
    // Clear the selected candidate so reopening the panel triggers a fresh
    // fetch and previous candidate state doesn't flash before the next load.
    fetchCandidateInCtx(null);
  }, [fetchCandidateInCtx]);

  // Send bulk messages
  const sendBulkMessages = useCallback(
    async (candidateIds: number[], channels: ('email' | 'whatsapp')[], content: MessageContent) => {
      setIsSendingMessages(true);
      try {
        const response = await adminApi.sendMessages(candidateIds, channels, content);
        toast({
          description: `Successfully sent ${response.totalSent} of ${response.totalProcessed} messages`,
        });
        
        if (response.totalFailed > 0) {
          toast({
            description: `${response.totalFailed} messages failed to send`,
            variant: 'destructive',
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send messages';
        toast({
          description: errorMessage,
          variant: 'destructive',
        });
        throw error;
      } finally {
        setIsSendingMessages(false);
      }
    },
    [toast]
  );

  return (
    <AdminContext.Provider
      value={{
        filterOptions,
        fetchFilterOptions,
        filters,
        setFilters,
        clearFilters,
        searchResults,
        searchCandidates,
        selectedCandidates,
        setSelectedCandidates,
        toggleCandidateSelection,
        clearSelectedCandidates,
        selectAllCandidates,
        slidePanel,
        openCandidatePanel,
        closeCandidatePanel,
        sendBulkMessages,
        isSendingMessages,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}


